import { StrictMode, Component, type ReactNode, type ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import { BrandConfigProvider, type ClientBrandConfig } from './context/BrandConfigContext';
import { ProductCacheProvider } from './ui-components';
import { MetricsFlywheel } from './components/showcase/MetricsFlywheel';
import { CustomerGraph } from './components/showcase/CustomerGraph';
import { CustomBrand } from './components/showcase/CustomBrand';
import { SalesAssociate } from './components/showcase/SalesAssociate';
import { GuardianProtocol } from './components/showcase/GuardianProtocol';
import { ChatDemoAnimation } from './components/ChatDemoAnimation';
import { ReelLoop } from './components/showcase/ReelLoop';
import { SkinIntelligence } from './components/showcase/SkinIntelligence';
import { VirtualTryOn } from './components/showcase/VirtualTryOn';
import { FitIntelligence } from './components/showcase/FitIntelligence';
import { ConversationAnalytics } from './components/showcase/ConversationAnalytics';
import { WebsitePersonalization } from './components/showcase/WebsitePersonalization';
import './ui-components/styles/design-tokens.css';
import './components/showcase/showcase-shared.css';

const LOCAL_FALLBACK_API = 'https://closera-liveboard-staging-571836375014.us-central1.run.app';
const apiParams = new URLSearchParams(window.location.search);
const apiOverride = apiParams.get('apiBaseUrl') || apiParams.get('api');
const isLocalPreview = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = apiOverride || import.meta.env.VITE_API_URL || (isLocalPreview ? LOCAL_FALLBACK_API : '');

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('Showcase error:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, color: 'red', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          <h2>Showcase Error</h2>
          <p>{this.state.error.message}</p>
          <p>{this.state.error.stack}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const JCREW_BRAND_CONFIG: ClientBrandConfig = {
  brandName: 'J.Crew',
  displayName: 'Closera AI',
  iconText: 'C',
  tagline: '',
  defaultPlaceholder: "Ask J.Crew's AI Stylist anything...",
  siteNavButtonText: 'Shop with J.Crew AI',
  colors: {
    accent: '#292929',
    accentHover: '#444444',
    accentLight: '#f5f3f0',
    avatarBackground: '#f5f3f0',
    avatarText: '#292929',
  },
  fonts: {
    brandFont: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    brandFontSize: '16px',
    brandFontWeight: 700,
    brandFontStyle: 'normal',
  },
  iconSvgPaths: [],
  iconViewBox: '0 0 24 24',
  suggestions: [],
  homeChips: [],
  collectionSuggestions: {},
  currency: 'USD',
  locale: 'en-US',
  features: { showSizeHelp: true, showBodyShapeSelector: true, showSizeChart: true, showSizeRecommendation: true, photoAction: 'virtualTryOn' },
  labels: { colorTerm: 'Color', materialTerm: 'Material', sizeTerm: 'Size', bundleTerm: 'Outfit' },
};

const CARDS: Record<string, React.FC<{ apiBaseUrl: string }>> = {
  'hero': ChatDemoAnimation,
  'metrics-flywheel': MetricsFlywheel,
  'customer-graph': CustomerGraph,
  'custom-brand': CustomBrand,
  'sales-associate': SalesAssociate,
  'guardian-protocol': GuardianProtocol,
  'skin-intelligence': SkinIntelligence,
  'virtual-try-on': VirtualTryOn,
  'fit-intelligence': FitIntelligence,
  'conversation-analytics': ConversationAnalytics,
  'website-personalization': WebsitePersonalization,
  'reel': ReelLoop,
};

function ShowcaseRouter({ apiBaseUrl }: { apiBaseUrl: string }) {
  const params = new URLSearchParams(window.location.search);
  const cardId = params.get('card') || '';
  const embed = params.get('embed') === 'true';
  // Reel mode is for booth displays — always fullpage, never embed/mobile.
  const isReel = cardId === 'reel';
  const fullpage = isReel ? true : params.get('fullpage') !== 'false';
  const mobile = !isReel && params.get('mobile') === 'true';

  const Card = CARDS[cardId];

  const rootClasses = [
    'showcase-root',
    fullpage && !embed ? 'fullpage' : '',
    embed && !isReel ? 'embed' : '',
    mobile ? 'mobile' : '',
  ].filter(Boolean).join(' ');

  if (!Card) {
    return (
      <div className={rootClasses}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <h2 style={{ marginBottom: 16, fontSize: 18 }}>Closera Showcase</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>Select a card:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            {Object.keys(CARDS).map(id => (
              <a
                key={id}
                href={`?card=${id}`}
                style={{ color: '#7dd3fc', textDecoration: 'none', fontSize: 15 }}
              >
                {id}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClasses}>
      <Card apiBaseUrl={apiBaseUrl} />
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrandConfigProvider config={JCREW_BRAND_CONFIG}>
        <ProductCacheProvider>
          <ShowcaseRouter apiBaseUrl={API_BASE_URL} />
        </ProductCacheProvider>
      </BrandConfigProvider>
    </ErrorBoundary>
  </StrictMode>
);
